import { getStore, RootState } from '../../../init-store';
import { bind } from './decorators';
import { EventEmitter } from './event-emitter';
import {
    CONNECTION_TIME_MEASURE,
    createDrawingStatsCounter,
    DRAWING_TIME_MEASURE,
    DrawingStats,
    DrawingStatsCounter,
    FRAME_TIME_MEASURE,
} from './performance';

const DEFAULT_COLLECT_STATS_INTERVAL = 10000;
const FPS_BUFFER_SIZE = 60;

export interface DrawingPerformanceStatsReport extends DrawingStats {
    numberOfParticipantsWithVideo: number;
    rtcConnectionTime?: number;
}

export class DrawingPerformanceStats extends EventEmitter {
    private _collectStatsTimer: ReturnType<typeof setInterval> | null = null;
    private _performanceObserver?: PerformanceObserver = undefined;
    private _drawingStatsCounter: DrawingStatsCounter;
    private _connectionTime: number | null = null;
    private _collectStatsInterval: number;
    constructor({ interval = DEFAULT_COLLECT_STATS_INTERVAL }: { interval?: number } = {}) {
        super();
        this._drawingStatsCounter = createDrawingStatsCounter(FPS_BUFFER_SIZE);
        this._collectStatsInterval = interval;
    }

    public startJob() {
        if (!this._collectStatsTimer) {
            this._collectStatsTimer = setInterval(this._collectStats, this._collectStatsInterval);
            this._runPerformanceObserver();
        }
    }

    public stopJob() {
        if (this._collectStatsTimer) {
            clearInterval(this._collectStatsTimer);
            this._collectStatsTimer = null;
            this._performanceObserver?.disconnect();
            delete this._performanceObserver;
        }
    }

    public destroy() {
        this.stopJob();
        this.removeAllListeners();
    }

    private _runPerformanceObserver() {
        const { countDrawingTime, countFrameRate } = this._drawingStatsCounter;

        this._performanceObserver = new PerformanceObserver((entries) => {
            entries.getEntriesByName(CONNECTION_TIME_MEASURE).forEach((connectionTime) => {
                this._connectionTime = connectionTime.duration;
            });
            entries.getEntriesByName(DRAWING_TIME_MEASURE).forEach((drawTime) => {
                countDrawingTime(drawTime.duration);
            });
            entries.getEntriesByName(FRAME_TIME_MEASURE).forEach((frameTime) => {
                countFrameRate(frameTime.duration);
            });
            // чистим измерения из кэша после обработки
            performance.clearMeasures(CONNECTION_TIME_MEASURE);
            performance.clearMeasures(DRAWING_TIME_MEASURE);
            performance.clearMeasures(FRAME_TIME_MEASURE);
        });
        this._performanceObserver.observe({ type: 'measure', buffered: true });
    }

    private _countParticipantsWithVideo(state: RootState) {
        const { participants, participantsByIds } = state.call.conference;
        return participants.reduce((acc, participantId) => {
            const participant = participantsByIds[participantId];
            if (participant?.video && !participant.video?.muted) {
                return acc + 1;
            }
            return acc;
        }, 0);
    }

    @bind
    private async _collectStats() {
        const state = getStore().getState();
        const drawingStats = this._drawingStatsCounter?.getCurrentDrawingStats();

        const numberOfParticipantsWithVideo = this._countParticipantsWithVideo(state);

        const statsReport: DrawingPerformanceStatsReport = {
            numberOfParticipantsWithVideo,
            ...drawingStats,
        };

        if (this._connectionTime) {
            statsReport.rtcConnectionTime = this._connectionTime;
            // чистим время подключения, чтобы не повторяться
            this._connectionTime = null;
        }

        this.emit('performance-stats', statsReport);
    }
}
